import React, { useMemo, useEffect, useState } from 'react' // Force reload
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Alert,
  AlertTitle,
  Chip,
  Tooltip,
  IconButton
} from '@mui/material'
import { Info as InfoIcon } from '@mui/icons-material'
import { Controller, Control, FormState } from 'react-hook-form'
import PsychrometricChart from '../../charts/PsychrometricChart'
// PsychrometricCalculator는 더 이상 직접 사용하지 않음 (ahuCalculationResults 사용)
import { generatePsychrometricChartData } from '../../../utils/psychroChartData'

interface AirflowSelectionTabProps {
  control: Control<any>
  formState: FormState<any>
  watch: (name: string) => any
  supplyAirSelectionCriteria: string
  outdoorAirSelectionCriteria: string
  selectedOutdoorCondition: string
  calculatedAirflows: {
    supplyAirflow: number
    returnAirflow: number
    outdoorAirflow: number
    exhaustAirflow: number
    pressurizationAirflow: number
    baseOutdoorAirflow: number
    minRequiredOutdoorAirflow: number
    wasAdjusted: boolean
  }
  ahuCalculationResults?: any
  currentProject?: any
  humidifierType?: string
  onPsychroDataCalculated?: (data: { points: any[], lines: any[], image?: string, metadata?: any, ahuId?: string }) => void
  currentAHUId?: string
  currentAHUData?: any
}

const AirflowSelectionTab: React.FC<AirflowSelectionTabProps> = ({
  control,
  formState,
  watch,
  supplyAirSelectionCriteria,
  outdoorAirSelectionCriteria,
  selectedOutdoorCondition,
  calculatedAirflows,
  ahuCalculationResults,
  currentProject,
  humidifierType,
  onPsychroDataCalculated,
  currentAHUId,
  currentAHUData
}) => {
  // 로컬에서 watch 값들을 가져와서 무한 렌더링 방지
  // watch 값들을 미리 추출하여 useMemo 의존성으로 사용
  const localSupplyAirSelectionCriteria = watch('supplyAirSelectionCriteria') || 'standard'
  const localOutdoorAirSelectionCriteria = watch('outdoorAirSelectionCriteria') || 'occupancy'
  const localSelectedOutdoorCondition = watch('outdoorDesignCondition')
  const localHeatingRoomTemp = watch('heatingRoomTemp')
  const localHeatingRoomHumidity = watch('heatingRoomHumidity')
  const localDirectOutdoorAirflow = watch('directOutdoorAirflow')
  const localSupplyAirflow = watch('supplyAirflow')
  const localHeatingLoad = watch('heatingLoad')
  const localRhReheatTemp = watch('rhReheatTemp')
  const localSensibleCoolingLoad = watch('sensibleCoolingLoad')
  const localLatentCoolingLoad = watch('latentCoolingLoad')
  const localCoolingRoomTemp = watch('coolingRoomTemp')
  const localSupplyTempDelta = watch('supplyTempDelta')
  const localDirectSupplyAirflow = watch('directSupplyAirflow')

  // RA Fan 위치를 GlobalState에서 가져오기 (Fan Selection 탭 데이터)
  // useState와 useEffect를 사용하여 데이터 변경 시 자동 업데이트
  const [localRaFanPosition, setLocalRaFanPosition] = useState<'None' | 'Inside' | 'Outside'>('Inside');

  useEffect(() => {
    const getRaFanPosition = (): 'None' | 'Inside' | 'Outside' => {
      if (!currentAHUId || !currentAHUData) {
        console.log('🔍 [AirflowSelectionTab] RA Fan Position: 기본값 (Inside) - AHU 데이터 없음');
        return 'Inside'; // 기본값
      }

      try {
        // GlobalState에서 fanSelectionData 읽기
        const raFanPosition = currentAHUData.fanSelectionData?.criteria?.raFanPosition;
        console.log('🔍 [AirflowSelectionTab] RA Fan Position 읽기:', {
          ahuId: currentAHUId,
          raFanPosition: raFanPosition,
          fanSelectionData: currentAHUData.fanSelectionData?.criteria
        });
        if (raFanPosition && ['None', 'Inside', 'Outside'].includes(raFanPosition)) {
          return raFanPosition as 'None' | 'Inside' | 'Outside';
        }
      } catch (error) {
        console.error('❌ [AirflowSelectionTab] RA Fan Position 조회 실패:', error);
      }

      console.log('🔍 [AirflowSelectionTab] RA Fan Position: 기본값 (Inside) - 유효하지 않은 값');
      return 'Inside'; // 기본값
    };

    const position = getRaFanPosition();
    console.log('🎯 [AirflowSelectionTab] RA Fan Position 설정:', position);
    setLocalRaFanPosition(position);

    // Fan Selection 탭에서 변경 시 자동 업데이트 (custom event)
    const handleStorageChange = () => {
      setLocalRaFanPosition(getRaFanPosition());
    };

    window.addEventListener('fan-selection-updated', handleStorageChange);

    return () => {
      window.removeEventListener('fan-selection-updated', handleStorageChange);
    };
  }, [currentAHUId, currentAHUData]);

  // 습공기 선도 데이터를 계산하는 useMemo - generatePsychrometricChartData 사용
  const psychrometricChartData = useMemo(() => {
    if (!ahuCalculationResults) return null;
    try {
      // formData 객체 구성
      const formData = {
        outdoorDesignCondition: localSelectedOutdoorCondition,
        heatingRoomTemp: localHeatingRoomTemp,
        heatingRoomHumidity: localHeatingRoomHumidity,
        directOutdoorAirflow: localDirectOutdoorAirflow,
        supplyAirflow: localSupplyAirflow,
        heatingLoad: localHeatingLoad,
        rhReheatTemp: localRhReheatTemp,
        supplyAirSelectionCriteria: localSupplyAirSelectionCriteria,
        outdoorAirSelectionCriteria: localOutdoorAirSelectionCriteria,
        // 사용자가 입력한 원래 실내 온도/습도를 사용 (계산 결과가 아님)
        coolingRoomTemp: watch('coolingRoomTemp') || 26,
        coolingRoomHumidity: watch('coolingRoomHumidity') || 55
      };

      // OUTDOOR_CONDITIONS 정의 (AHUSelectionForm과 동일하게)
      const OUTDOOR_CONDITIONS = [
      {
        code: 'A',
        name: '표준 조건',
        summer: { temperature: 31.2, humidity: 69, dailyRange: 10 },
        winter: { temperature: -11.9, humidity: 69 }
      },
      {
        code: 'B',
        name: '일반 조건',
        summer: { temperature: 33.0, humidity: 59.8, dailyRange: 9 },
        winter: { temperature: -14.9, humidity: 69 }
      },
    ]

      // 냉방 모드 감지: 냉방 현열/잠열 부하가 0.001보다 크면 냉방 모드
      const sensibleLoad = parseFloat(String(localSensibleCoolingLoad).replace(/,/g, '')) || 0;
      const latentLoad = parseFloat(String(localLatentCoolingLoad).replace(/,/g, '')) || 0;
      const isCoolingMode = sensibleLoad > 0.001 || latentLoad > 0.001;

      // generatePsychrometricChartData 함수로 계산 (단일 소스)
      const chartData = generatePsychrometricChartData({
        formData,
        ahuCalculationResults,
        OUTDOOR_CONDITIONS,
        supplyAirSelectionCriteria: localSupplyAirSelectionCriteria,
        outdoorAirSelectionCriteria: localOutdoorAirSelectionCriteria,
        humidifierType: 'None',
        isCoolingMode: isCoolingMode,
        raFanType: localRaFanPosition
      });


      // 난방 상태점들 확인 (특히 m1, l2)
      const heatingPoints = chartData.points?.filter(p => ['o2', 'r2', 'm1', 'l2', 'l3'].includes(p.name)) || [];
      //   name: p.name,
      //   temp: p.dryBulbTemp,
      //   rh: p.relativeHumidity,
      //   hr: p.humidityRatio
      // })));

      return chartData;
    } catch (error) {
      console.error('❌ AirflowSelectionTab generatePsychrometricChartData 실패:', error);
      return null;
    }
  }, 
  [ahuCalculationResults, localSelectedOutdoorCondition, localHeatingRoomTemp, localHeatingRoomHumidity, localDirectOutdoorAirflow, localSupplyAirflow, localHeatingLoad, localRhReheatTemp, localSupplyAirSelectionCriteria, localOutdoorAirSelectionCriteria]);


  // 제거: onChartCaptured 콜백이 직접 처리하므로 useEffect 불필요
  // (이 useEffect가 무한 루프를 일으켰음)

  // 실시간 외기량 계산 (원본 AHUSelectionForm의 calculatedAirflows 로직과 동일)
  const realTimeOutdoorAirflow = useMemo(() => {
    const formData = {
      occupantCount: watch('occupantCount'),
      airflowPerPerson: watch('airflowPerPerson'),
      directOutdoorAirflow: watch('directOutdoorAirflow'),
      exhaustAirflow: watch('exhaustAirflow'),
      pressurizationAirflow: watch('pressurizationAirflow')
    }

    let baseOutdoorAirflow = 0;

    // 외기량 계산 (원본과 동일한 로직)
    switch (localOutdoorAirSelectionCriteria) {
      case 'occupancy': // 인원수 기준 (m³/h/인)
        {
          const occupants = parseFloat(formData.occupantCount) || 0;
          const airflowPerson = parseFloat(formData.airflowPerPerson) || 25;
          baseOutdoorAirflow = occupants * airflowPerson;
          break;
        }
      case 'direct': // 직접입력
        {
          baseOutdoorAirflow = parseFloat(formData.directOutdoorAirflow) || 0;
          break;
        }
      case 'standard': // 급기량과 동일
        {
          baseOutdoorAirflow = calculatedAirflows.supplyAirflow;
          break;
        }
      default:
        baseOutdoorAirflow = 0;
    }

    // 배기량과 가압량을 고려한 최소 외기량 계산
    const exhaustAirflow = parseFloat(formData.exhaustAirflow) || 0;
    const pressurizationAirflow = parseFloat(formData.pressurizationAirflow) || 0;
    const minRequiredOutdoorAirflow = exhaustAirflow + pressurizationAirflow;

    // 최종 외기량 결정 (최소 요구량보다 작으면 조정)
    let finalOutdoorAirflow = Math.max(baseOutdoorAirflow, minRequiredOutdoorAirflow);

    // 외기량이 급기량을 초과할 수 없음 (HVAC 기본 원리: 급기량 = 환기량 + 외기량)
    const supplyAirflow = calculatedAirflows.supplyAirflow;
    let isExceeded = false;
    if (supplyAirflow > 0 && finalOutdoorAirflow > supplyAirflow) {
      finalOutdoorAirflow = supplyAirflow;
      isExceeded = true;
    }

    return {
      baseOutdoorAirflow,
      minRequiredOutdoorAirflow,
      outdoorAirflow: finalOutdoorAirflow,
      isExceededSupplyAirflow: isExceeded
    };
  }, [
    localOutdoorAirSelectionCriteria,
    watch('occupantCount'),
    watch('airflowPerPerson'),
    watch('directOutdoorAirflow'),
    watch('exhaustAirflow'),
    watch('pressurizationAirflow'),
    calculatedAirflows.supplyAirflow
  ])

  // 실시간 환기량 계산 (원본과 동일: 급기량 - 배기량 - 가압량)
  const realTimeReturnAirflow = useMemo(() => {
    const exhaustAirflow = parseFloat(watch('exhaustAirflow')) || 0;
    const pressurizationAirflow = parseFloat(watch('pressurizationAirflow')) || 0;

    // VBA 환기량 계산 로직: RAvol = 급기량 - 배기량 - 가압량
    const calculatedReturnAirflow = Math.max(0, calculatedAirflows.supplyAirflow - exhaustAirflow - pressurizationAirflow);

    return calculatedReturnAirflow;
  }, [
    calculatedAirflows.supplyAirflow,
    watch('exhaustAirflow'),
    watch('pressurizationAirflow')
  ])
  return (
    <Grid container spacing={3}>
      {/* 1. 공조 시스템 정보 */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" component="div" gutterBottom sx={{
            bgcolor: 'grey.200', fontWeight: 600,
            p: 1,
            mb: 2,
            borderRadius: 1
          }}>
            1. 공조 시스템 정보
          </Typography>

          {/* (1) 공간 및 인원 특성 */}
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
            (1) 공간 및 인원 특성
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>공조면적 (m²)</Typography>
              <Controller
                name="conditionedArea"
                control={control}
                rules={{
                  required: '공조면적은 필수입니다',
                  min: { value: 0.1, message: '0보다 큰 값을 입력해주세요' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    type="text"
                    value={field.value ? Number(field.value).toLocaleString() : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, '');
                      if (value === '' || /^\d+\.?\d*$/.test(value)) {
                        field.onChange(value === '' ? '' : value);
                      }
                    }}
                    error={!!formState.errors.conditionedArea}
                    helperText={formState.errors.conditionedArea?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>천장고 (m)</Typography>
              <Controller
                name="ceilingHeight"
                control={control}
                rules={{
                  required: '층고는 필수입니다',
                  min: { value: 1.0, message: '1.0m 이상의 값을 입력해주세요' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    inputProps={{ min: 1.0, step: 0.1 }}
                    error={!!formState.errors.ceilingHeight}
                    helperText={formState.errors.ceilingHeight?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>인원수 (명)</Typography>
              <Controller
                name="occupantCount"
                control={control}
                rules={{
                  required: '인원수는 필수입니다',
                  min: { value: 1, message: '1명 이상의 값을 입력해주세요' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    error={!!formState.errors.occupantCount}
                    helperText={formState.errors.occupantCount?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* (2) 공조 부하 */}
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
            (2) 공조 부하
          </Typography>
          {localSupplyAirSelectionCriteria === 'direct' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              현재 "급기량 선정 기준"이 "직접입력"으로 설정되어 있어 부하값 입력이 비활성화되었습니다.
              <br />
              부하값을 입력하려면 아래 "4. 급기량 선정 기준"에서 "급기 상태점"을 선택해주세요.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>냉방 현열 부하(W)</Typography>
              <Controller
                name="sensibleCoolingLoad"
                control={control}
                rules={{
                  required: '현열부하는 필수입니다',
                  min: { value: 0, message: '0 이상의 값을 입력해주세요' }
                }}
                render={({ field: { onChange, value, ...restField } }) => (
                  <TextField
                    {...restField}
                    value={value ? parseFloat(value).toLocaleString() : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, '');
                      onChange(rawValue);
                    }}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, step: 1 }}
                    disabled={localSupplyAirSelectionCriteria === 'direct'}
                    sx={localSupplyAirSelectionCriteria === 'direct' ? { bgcolor: '#f5f5f5' } : {}}
                    error={!!formState.errors.sensibleCoolingLoad}
                    helperText={formState.errors.sensibleCoolingLoad?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>냉방 잠열 부하(W)</Typography>
              <Controller
                name="latentCoolingLoad"
                control={control}
                rules={{
                  required: '잠열부하는 필수입니다',
                  min: { value: 0, message: '0 이상의 값을 입력해주세요' }
                }}
                render={({ field: { onChange, value, ...restField } }) => (
                  <TextField
                    {...restField}
                    value={value ? parseFloat(value).toLocaleString() : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, '');
                      onChange(rawValue);
                    }}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, step: 1 }}
                    disabled={localSupplyAirSelectionCriteria === 'direct'}
                    sx={localSupplyAirSelectionCriteria === 'direct' ? { bgcolor: '#f5f5f5' } : {}}
                    error={!!formState.errors.latentCoolingLoad}
                    helperText={formState.errors.latentCoolingLoad?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                  천장속 조명부하(W)
                </Typography>
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        천장속(Plenum) 조명부하 — RA 온도상승용 (Q1)
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                        • 목적: 천장속(플레넘)에 설치된 조명기구의 발열을 환기(RA) 경로에 반영하여
                        RA 온도상승(RAte)을 계산합니다.
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                        • 연결: OUTPUT SHEETS 의 RA' Condition 블록에서
                        <code> RA Load = Q1 + Q3 </code>의 <b>Q1</b>로 사용됩니다.
                      </Typography>
                      <Typography variant="caption" component="div">
                        • 값 &gt; 0 : RA 온도상승 적용 / 값 = 0 : 적용 무시
                      </Typography>
                    </Box>
                  }
                >
                  <InfoIcon fontSize="small" sx={{ color: 'primary.main', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Controller
                name="ventilationLightingLoad"
                control={control}
                rules={{
                  min: { value: 0, message: '0 이상의 값을 입력해주세요' }
                }}
                render={({ field: { onChange, value, ...restField } }) => (
                  <TextField
                    {...restField}
                    value={value ? parseFloat(value).toLocaleString() : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, '');
                      onChange(rawValue);
                    }}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, step: 1 }}
                    disabled={localSupplyAirSelectionCriteria === 'direct'}
                    sx={localSupplyAirSelectionCriteria === 'direct' ? { bgcolor: '#f5f5f5' } : {}}
                    error={!!formState.errors.ventilationLightingLoad}
                    helperText={formState.errors.ventilationLightingLoad?.message}
                  />
                )}
              />
              {/* 사용자 설명: 천장속 조명부하 입력 가이드 */}
              <Typography
                variant="caption"
                component="div"
                sx={{
                  mt: 0.75,
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  lineHeight: 1.35,
                  display: 'block'
                }}
              >
                천장속(Plenum) 조명으로 인한 <b>RA 온도상승</b>을 적용할 때 입력합니다.
                <br />
                입력 시 OUTPUT SHEETS 의 <code>RA Load = Q1 + Q3</code> 에서
                <b> Q1</b>로 반영됩니다. <b>0</b>이면 RA 온도상승 적용이 무시됩니다.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>난방 부하(W)</Typography>
              <Controller
                name="heatingLoad"
                control={control}
                rules={{
                  min: { value: 0, message: '0 이상의 값을 입력해주세요' }
                }}
                render={({ field: { onChange, value, ...restField } }) => (
                  <TextField
                    {...restField}
                    value={value ? parseFloat(value).toLocaleString() : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, '');
                      onChange(rawValue);
                    }}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, step: 1 }}
                    disabled={localSupplyAirSelectionCriteria === 'direct'}
                    sx={localSupplyAirSelectionCriteria === 'direct' ? { bgcolor: '#f5f5f5' } : {}}
                    error={!!formState.errors.heatingLoad}
                    helperText={formState.errors.heatingLoad?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* SF/RF 부하 적용 비율 */}
          <Grid container spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
            {/* 급기팬 온도상승 적용 비율 */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}>
                  급기팬 부하 온도상승:
                </Typography>
                <Controller
                  name="sfLoadPercentage"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      type="number"
                      sx={{ width: 80 }}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                    />
                  )}
                />
                <Typography component="span">%</Typography>
              </Box>
            </Grid>

            {/* 환기팬 온도상승 적용 비율 */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}>
                  환기팬 부하 온도상승:
                </Typography>
                <Controller
                  name="rfLoadPercentage"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      type="number"
                      sx={{ width: 80 }}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                    />
                  )}
                />
                <Typography component="span">%</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* 2. 실내 온습도 */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" component="div" gutterBottom sx={{
            bgcolor: 'grey.200', fontWeight: 600,
            p: 1,
            mb: 2,
            borderRadius: 1
          }}>
            2. 실내 온습도
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>냉방</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Controller
                  name="coolingRoomTemp"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      type="number"
                      placeholder="26"
                      sx={{ width: 80 }}
                      inputProps={{ step: 0.1 }}
                    />
                  )}
                />
                <Typography component="span">℃ DB,</Typography>
                <Controller
                  name="coolingRoomHumidity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      type="number"
                      placeholder="55"
                      sx={{ width: 80 }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  )}
                />
                <Typography component="span">% RH</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>난방</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Controller
                  name="heatingRoomTemp"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      type="number"
                      placeholder="20"
                      sx={{ width: 80 }}
                      inputProps={{ step: 0.1 }}
                    />
                  )}
                />
                <Typography component="span">℃ DB,</Typography>
                <Controller
                  name="heatingRoomHumidity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      type="number"
                      placeholder="40"
                      sx={{ width: 80 }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  )}
                />
                <Typography component="span">% RH</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* 3. 외기량 선정 기준 */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" component="div" gutterBottom sx={{
            bgcolor: 'grey.200', fontWeight: 600,
            p: 1,
            mb: 2,
            borderRadius: 1
          }}>
            3. 외기량 선정 기준
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>외기량 선정 기준</Typography>
              <Controller
                name="outdoorAirSelectionCriteria"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={value || 'occupancy'}
                      onChange={onChange}
                      row
                    >
                      <FormControlLabel value="occupancy" control={<Radio />} label="인원수 (m³/h/인)" />
                      <FormControlLabel value="direct" control={<Radio />} label="직접입력 (m³/h)" />
                      <FormControlLabel value="standard" control={<Radio />} label="급기량과 동일" />
                    </RadioGroup>
                  </FormControl>
                )}
              />
              {/* 인원수 기준 입력 - 인원수 모드일 때만 표시 */}
              {localOutdoorAirSelectionCriteria === 'occupancy' && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" component="span">인원수:</Typography>
                  <Controller
                    name="occupantCount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        sx={{ width: 80 }}
                        InputProps={{
                          readOnly: true,
                          sx: { bgcolor: '#e6f3ff' }
                        }}
                      />
                    )}
                  />
                  <Typography variant="body2" component="span">인 ×</Typography>
                  <Controller
                    name="airflowPerPerson"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        sx={{ width: 80 }}
                      />
                    )}
                  />
                  <Typography variant="body2" component="span">m³/h/인</Typography>
                </Box>
              )}

              {/* 직접입력 모드일 때만 표시 */}
              {localOutdoorAirSelectionCriteria === 'direct' && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>외기량 (m³/h)</Typography>
                  <Controller
                    name="directOutdoorAirflow"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, step: 100 }}
                        placeholder="8700"
                      />
                    )}
                  />
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>계산된 외기량</Typography>
              <Box sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 1 }}>
                <Typography variant="h6" component="div" color="secondary">
                  {Math.round(realTimeOutdoorAirflow.outdoorAirflow).toLocaleString()} m³/h
                </Typography>
                <Typography variant="body2" component="div" color="textSecondary" sx={{ mt: 1 }}>
                  {localOutdoorAirSelectionCriteria === 'occupancy' && (
                    <>기본 외기량: {Math.round(realTimeOutdoorAirflow.baseOutdoorAirflow).toLocaleString()} m³/h</>
                  )}
                  {realTimeOutdoorAirflow.outdoorAirflow > realTimeOutdoorAirflow.baseOutdoorAirflow && (
                    <><br />최소 요구량: {Math.round(realTimeOutdoorAirflow.minRequiredOutdoorAirflow).toLocaleString()} m³/h</>
                  )}
                </Typography>
                <Typography variant="body2" component="div" color="textSecondary" sx={{ mt: 1 }}>
                  현재 선택된 외기조건:
                  <Chip
                    label={localSelectedOutdoorCondition || '미선택'}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* 4. 급기량 선정 기준 */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" component="div" gutterBottom sx={{
            bgcolor: 'grey.200', fontWeight: 600,
            p: 1,
            mb: 2,
            borderRadius: 1
          }}>
            4. 급기량 선정 기준
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>급기량 선정 기준</Typography>
              <Controller
                name="supplyAirSelectionCriteria"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={value || 'standard'}
                      onChange={onChange}
                      row
                    >
                      <FormControlLabel value="standard" control={<Radio />} label="급기 상태점" />
                      <FormControlLabel value="direct" control={<Radio />} label="직접입력 (m³/h)" />
                    </RadioGroup>
                  </FormControl>
                )}
              />

              {/* 급기 상태점 모드일 때만 표시되는 입력들 */}
              {localSupplyAirSelectionCriteria === 'standard' && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ mb: 1 }}>급기(△T)</Typography>
                      <Controller
                        name="supplyTempDelta"
                        control={control}
                        rules={{
                          required: '급기(△T)는 필수입니다'
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 5, max: 20, step: 0.1 }}
                            placeholder="10"
                            error={!!formState.errors.supplyTempDelta}
                            helperText={formState.errors.supplyTempDelta?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="body2" component="div">SA Fan(△T) (계산값)</Typography>
                        <Tooltip
                          title="급기량과 FAN 선정 탭의 팬 사양을 기반으로 자동 계산됩니다."
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ p: 0.3 }}>
                            <InfoIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        value={
                          calculatedAirflows.supplyAirflow === 0
                            ? '-'
                            : ahuCalculationResults?.supplyFanTempRise?.toFixed(2) || '0.00'
                        }
                        InputProps={{
                          readOnly: true,
                          sx: { bgcolor: '#f5f5f5' }
                        }}
                      />
                      <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                        팬 동력에 의한 온도 상승 (FAN 선정 탭 연동)
                      </Typography>
                    </Grid>

                    {/* 재열 Coil(△T) - 재열 코일이 None이 아닐 때만 표시 */}
                    {watch('reheatingCoilType') !== 'None' && (
                      <Grid item xs={3}>
                        <Typography variant="body2" component="div" sx={{ mb: 1 }}>재열 Coil(△T)</Typography>
                        <Controller
                          name="rhReheatTemp"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              size="small"
                              fullWidth
                              type="number"
                              inputProps={{
                                min: 0,
                                max: 200,
                                step: 0.1
                              }}
                              placeholder="0"
                            />
                          )}
                        />
                        <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                          재열 코일에 의한 온도 상승 (℃)
                        </Typography>
                      </Grid>
                    )}

                    {/* 급기 상태 / A.C - 급기량이 계산되었을 때만 표시 */}
                    {currentProject && ahuCalculationResults && calculatedAirflows.supplyAirflow > 0 && (() => {
                      // 냉방 모드 감지: 냉방 부하(현열 + 잠열)가 0보다 크면 냉방 모드
                      const sensibleLoad = parseFloat(String(localSensibleCoolingLoad).replace(/,/g, '')) || 0;
                      const latentLoad = parseFloat(String(localLatentCoolingLoad).replace(/,/g, '')) || 0;
                      const isCoolingMode = sensibleLoad > 0 || latentLoad > 0;

                      return (
                        <>
                          <Grid item xs={isCoolingMode ? 6 : 12}>
                            <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>급기 상태 / A.C</Typography>
                            <TextField
                              size="small"
                              value={ahuCalculationResults?.supplyAir ?
                                `${ahuCalculationResults.supplyAir.dryBulbTemp.toFixed(1)}℃ DB, ${ahuCalculationResults.supplyAir.relativeHumidity.toFixed(1)}% RH${ahuCalculationResults.airChangeRate ? ` / ${ahuCalculationResults.airChangeRate}회/h` : ''}` :
                                "계산 대기 중..."
                              }
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                sx: { bgcolor: '#ccffcc' }
                              }}
                            />
                          </Grid>
                          {/* ADP/Bypass F.는 냉방 모드일 때만 표시 */}
                          {isCoolingMode && (
                            <Grid item xs={6}>
                              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>ADP/Bypass F.</Typography>
                              <TextField
                                size="small"
                                value={ahuCalculationResults?.adp ?
                                  ahuCalculationResults.adp.message :
                                  "계산 대기 중..."
                                }
                                fullWidth
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    bgcolor: ahuCalculationResults?.adp?.valid ? '#ccffcc' : '#ffcccc'
                                  }
                                }}
                              />
                            </Grid>
                          )}
                        </>
                      );
                    })()}
                  </Grid>
                </Box>
              )}

              {/* 직접입력 모드일 때만 표시되는 입력 */}
              {localSupplyAirSelectionCriteria === 'direct' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>급기량 (m³/h)</Typography>
                  <Controller
                    name="directSupplyAirflow"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        fullWidth
                        type="text"
                        value={field.value ? Number(field.value).toLocaleString() : ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/,/g, '');
                          if (value === '' || /^\d+$/.test(value)) {
                            field.onChange(value === '' ? '' : value);
                          }
                        }}
                        placeholder="8,700"
                      />
                    )}
                  />
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {localSupplyAirSelectionCriteria === 'standard' ? '계산된 급기량' : '직접 입력된 급기량'}
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="h6" component="div" color="primary">
                  {calculatedAirflows.supplyAirflow === 0 ? '-' : calculatedAirflows.supplyAirflow.toLocaleString()} m³/h
                </Typography>
                <Typography variant="body2" component="div" color="textSecondary" sx={{ mt: 1 }}>
                  {localSupplyAirSelectionCriteria === 'standard' ? (
                    <>
                      {calculatedAirflows.supplyAirflow === 0 ? (
                        <>
                          • 급기(△T)와 부하값을 입력하면 급기량이 계산됩니다
                          <br />
                          • 냉방: 급기량(m³/h) = (냉방 현열 부하 + 환기 조명부하)(W) / (0.335 × ΔT℃)
                          <br />
                          • 난방: 급기량(m³/h) = 난방 부하(W) / (0.335 × ΔT℃)
                        </>
                      ) : (
                        (() => {
                          const sensibleLoad = parseFloat(localSensibleCoolingLoad) || 0;
                          const latentLoad = parseFloat(String(watch('latentCoolingLoad')).replace(/,/g, '')) || 0;
                          const ventilationLoad = parseFloat(String(watch('ventilationLightingLoad')).replace(/,/g, '')) || 0;
                          const heatingLoad = parseFloat(localHeatingLoad) || 0;
                          const supplyDelta = parseFloat(localSupplyTempDelta) || 10;

                          // 냉방 부하(현열 + 잠열)가 모두 0이면 난방 기준
                          const isCoolingMode = sensibleLoad > 0 || latentLoad > 0;

                          if (isCoolingMode) {
                            // 냉방 모드: 현열 부하 + 환기 조명부하
                            return (
                              <>
                                • {Math.round(calculatedAirflows.supplyAirflow).toLocaleString()} m³/h = Round(({sensibleLoad.toLocaleString()} + {ventilationLoad.toLocaleString()}) W / (0.335 × {supplyDelta} ℃))
                                <br />
                                • 냉방 급기량(m³/h) = (냉방 현열 부하 + 환기 조명부하)(W) / (0.335 × ΔT℃)
                                <br />
                                • 0.335 = 공기밀도(1.2 kg/m³) × 공기정압비열(1.006 kJ/kg·K) × 1000/3600
                              </>
                            );
                          } else {
                            // 난방 모드: 난방 부하만 사용
                            return (
                              <>
                                • {Math.round(calculatedAirflows.supplyAirflow).toLocaleString()} m³/h = Round({heatingLoad.toLocaleString()} W / (0.335 × {supplyDelta} ℃))
                                <br />
                                • 난방 급기량(m³/h) = 난방 부하(W) / (0.335 × ΔT℃)
                                <br />
                                • 0.335 = 공기밀도(1.2 kg/m³) × 공기정압비열(1.006 kJ/kg·K) × 1000/3600
                                <br />
                                • ΔT = 급기온도 - 외기온도 (현재: {supplyDelta}℃)
                              </>
                            );
                          }
                        })()
                      )}
                    </>
                  ) : (
                    <>
                      • 직접 입력값: {(parseFloat(localDirectSupplyAirflow) || 0).toLocaleString()} m³/h
                      <br />
                      • 부하 계산 대신 사용자가 입력한 급기량을 사용합니다
                    </>
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* 5. 공조기 풍량 */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" component="div" gutterBottom sx={{
            bgcolor: 'grey.200', fontWeight: 600,
            p: 1,
            mb: 2,
            borderRadius: 1
          }}>
            5. 공조기 풍량
          </Typography>
          <Grid container spacing={2}>
            {/* 배기 용도, 배기량, 가압량 입력 */}
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>배기 용도</Typography>
              <Controller
                name="exhaustPurpose"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    placeholder="배기 용도 입력"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>배기량(CMH)</Typography>
              <Controller
                name="exhaustAirflow"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 100 }}
                    placeholder="0"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>가압량(CMH)</Typography>
              <Controller
                name="pressurizationAirflow"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 100 }}
                    placeholder="0"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* 6. 선정 공기 계산 결과 - 계산 결과가 있을 때만 표시 */}
      {currentProject && ahuCalculationResults && (
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" component="div" gutterBottom sx={{
              bgcolor: 'grey.200', fontWeight: 600,
              p: 1,
              mb: 2,
              borderRadius: 1
            }}>
              6. 선정 공기 계산 결과
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>B1F 상가</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                      급기량(m³/h) {localSupplyAirSelectionCriteria === 'standard' ? '(계산값)' : '(입력값)'}
                    </Typography>
                    <TextField
                      size="small"
                      value={calculatedAirflows.supplyAirflow === 0 ? '-' : Math.round(calculatedAirflows.supplyAirflow).toLocaleString()}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        sx: {
                          bgcolor: localSupplyAirSelectionCriteria === 'standard' ? '#ccffcc' : '#ffffcc'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                      외기량(m³/h) {localOutdoorAirSelectionCriteria === 'direct' ? '(입력값)' : '(계산값)'}
                    </Typography>
                    <TextField
                      size="small"
                      value={Math.round(realTimeOutdoorAirflow.outdoorAirflow).toLocaleString()}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        sx: {
                          bgcolor: localOutdoorAirSelectionCriteria === 'direct' ? '#ffffcc' : '#ccffcc'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                      환기량(m³/h) (계산값)
                    </Typography>
                    <TextField
                      size="small"
                      value={Math.round(realTimeReturnAirflow).toLocaleString()}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        sx: { bgcolor: '#ccffcc' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                      배기량(m³/h) (입력값)
                    </Typography>
                    <TextField
                      size="small"
                      value={Math.round(parseFloat(watch('exhaustAirflow')) || 0).toLocaleString()}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        sx: { bgcolor: '#ffffcc' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                      가압량(m³/h) (입력값)
                    </Typography>
                    <TextField
                      size="small"
                      value={Math.round(parseFloat(watch('pressurizationAirflow')) || 0).toLocaleString()}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        sx: { bgcolor: '#ffffcc' }
                      }}
                    />
                  </Grid>
                </Grid>
                {realTimeOutdoorAirflow.isExceededSupplyAirflow && (
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Alert severity="warning">
                      <AlertTitle>외기량 자동 조정 (급기량 초과)</AlertTitle>
                      외기량이 급기량을 초과하여 자동으로 급기량과 동일하게 조정되었습니다.
                      <br />
                      <strong>급기량:</strong> {Math.round(calculatedAirflows.supplyAirflow).toLocaleString()} m³/h
                      <br />
                      <strong>조정된 외기량:</strong> {Math.round(realTimeOutdoorAirflow.outdoorAirflow).toLocaleString()} m³/h
                    </Alert>
                  </Grid>
                )}
                {realTimeOutdoorAirflow.outdoorAirflow > realTimeOutdoorAirflow.baseOutdoorAirflow && !realTimeOutdoorAirflow.isExceededSupplyAirflow && (
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Alert severity="info">
                      <AlertTitle>외기량 자동 조정</AlertTitle>
                      외기량이 배기량+가압량({Math.round(realTimeOutdoorAirflow.minRequiredOutdoorAirflow).toLocaleString()} m³/h)보다 작아 자동으로 조정되었습니다.
                      <br />
                      <strong>기본 외기량:</strong> {Math.round(realTimeOutdoorAirflow.baseOutdoorAirflow).toLocaleString()} m³/h →
                      <strong> 조정된 외기량:</strong> {Math.round(realTimeOutdoorAirflow.outdoorAirflow).toLocaleString()} m³/h
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* 7. 습공기 선도 - 계산 결과가 있을 때만 표시 */}
      <Grid item xs={12}>
        
        {(() => {
          // 급기량이 0이거나 계산 결과가 없으면 빈 차트 반환
          if (calculatedAirflows.supplyAirflow === 0) {
            return (
              <Paper sx={{ p: 2 }}>
                
                <Typography component="div" color="text.secondary">
                  급기량을 계산하면 습공기 선도가 표시됩니다.
                </Typography>
              </Paper>
            );
          }

          // ahuCalculationResults가 없으면 빈 차트 반환
          if (!ahuCalculationResults || !ahuCalculationResults.outdoorAir || !ahuCalculationResults.returnAir || !ahuCalculationResults.supplyAir) {
            return (
              <Paper sx={{ p: 2 }}>
                <Typography component="div" color="text.secondary">계산 결과가 필요합니다.</Typography>
              </Paper>
            );
          }

          // 필요한 값들 미리 추출 (무한 렌더링 방지)
          const OUTDOOR_CONDITIONS = [
            { code: 'A', region: '서울', summer: { temperature: 32.6, humidity: 69.8, dailyRange: 10.8 }, winter: { temperature: -12.0, humidity: 71.4 } },
            { code: 'B', region: '인천', summer: { temperature: 30.9, humidity: 79.4, dailyRange: 7.4 }, winter: { temperature: -10.6, humidity: 68.8 } },
            { code: 'C', region: '수원', summer: { temperature: 31.6, humidity: 76.8, dailyRange: 10.3 }, winter: { temperature: -13.1, humidity: 70.8 } },
            { code: 'D', region: '춘천', summer: { temperature: 31.5, humidity: 73.6, dailyRange: 12.1 }, winter: { temperature: -16.4, humidity: 70.9 } },
            { code: 'E', region: '강릉', summer: { temperature: 29.7, humidity: 79.0, dailyRange: 7.8 }, winter: { temperature: -5.4, humidity: 62.5 } },
            { code: 'F', region: '대전', summer: { temperature: 33.4, humidity: 71.4, dailyRange: 11.2 }, winter: { temperature: -11.8, humidity: 67.5 } }
          ];

          const outdoorDesignCondition = watch('outdoorDesignCondition');
          const rhReheatTemp = parseFloat(watch('rhReheatTemp')) || 0;
          const supplyAirflow = parseFloat(watch('supplyAirflow')) || 0; // 난방 라인 표시 조건 체크용
          const preheatingCoilType = watch('preheatingCoilType') || 'None';
          const reheatingCoilType = watch('reheatingCoilType') || 'None';

          // TA (Total Air) 계산: 배기량 + 가압량
          const exhaustAirflow = parseFloat(watch('exhaustAirflow')) || 0;
          const pressurizationAirflow = parseFloat(watch('pressurizationAirflow')) || 0;
          const totalAir = exhaustAirflow + pressurizationAirflow;

          // psychrometricChartData에서 계산된 points와 lines 직접 사용
          if (psychrometricChartData) {
            // 캡처 시점의 AHU ID를 클로저로 고정 (비동기 처리 중 공조기 전환 방지)
            const captureTimeAHUId = currentAHUId;

            // 냉방 모드 감지: 냉방 현열/잠열 부하가 0.001보다 크면 냉방 모드
            const sensibleLoad = parseFloat(String(localSensibleCoolingLoad).replace(/,/g, '')) || 0;
            const latentLoad = parseFloat(String(localLatentCoolingLoad).replace(/,/g, '')) || 0;
            const isCoolingMode = sensibleLoad > 0.001 || latentLoad > 0.001;

            return (
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fff' }}>
                <Typography variant="h6" component="div" gutterBottom sx={{
              bgcolor: 'grey.200', fontWeight: 600,
              p: 1,
              mb: 2,
              borderRadius: 1
            }}>
          7. 습공기 선도 (Psychrometric Chart)
        </Typography>
                <PsychrometricChart
                  points={psychrometricChartData.points}
                  lines={psychrometricChartData.lines}
                  elevation={parseFloat(currentProject?.altitude || '0')}
                  winterOutdoorTemp={OUTDOOR_CONDITIONS.find((c: any) => c.code === (outdoorDesignCondition || 'A'))?.winter.temperature || -12}
                  raFanType={localRaFanPosition as 'None' | 'Inside' | 'Outside'}
                  isCoolingMode={isCoolingMode}
                  humidifierType={humidifierType}
                  preheatingCoilType={preheatingCoilType}
                  reheatingCoilType={reheatingCoilType}
                  saFlow={Math.round(calculatedAirflows.supplyAirflow)}
                  raFlow={Math.round(realTimeReturnAirflow)}
                  eaFlow={Math.round(exhaustAirflow)}
                  taFlow={Math.round(totalAir)}
                  oaFlowMin={Math.round(realTimeOutdoorAirflow.outdoorAirflow)}
                  oaFlowMax={Math.round(calculatedAirflows.supplyAirflow)}
                  chartId={captureTimeAHUId ? `psychrometric-chart-${captureTimeAHUId}` : 'psychrometric-chart'}
                  showStatePoints={true}  // 상태점 정보를 차트 컴포넌트 내부에 표시 (이미지 캡처 시에는 제외됨)
                  onChartCaptured={(capturedData) => {
                    // 캡처된 고해상도 이미지와 JSON 데이터를 상위로 전달
                    // captureTimeAHUId를 사용하여 캡처 시점의 공조기에 저장
                    if (onPsychroDataCalculated) {
                      onPsychroDataCalculated({
                        points: capturedData.points,
                        lines: capturedData.lines,
                        image: capturedData.image,
                        metadata: capturedData.metadata,
                        ahuId: captureTimeAHUId  // 클로저로 고정된 AHU ID 사용
                      });
                    }
                  }}
                />
              </Box>
            );
          }

          // Fallback: psychrometricChartData가 없을 때만 하드코딩된 points 사용
          const psychroChartPoints = [
            // 냉방용 외기 상태점 (OA)
            {
              name: '외기(OA)',
              dryBulbTemp: ahuCalculationResults.outdoorAir.dryBulbTemp,
              relativeHumidity: ahuCalculationResults.outdoorAir.relativeHumidity,
              color: '#FF6B6B',
              size: 6
            },
            // 냉방용 혼합공기 상태점 (MA)
            {
              name: '혼합(MA)',
              dryBulbTemp: ahuCalculationResults.mixedAir.dryBulbTemp,
              relativeHumidity: ahuCalculationResults.mixedAir.relativeHumidity,
              color: '#45B7D1',
              size: 6
            },
            // 냉방용 냉각코일 후 상태점 (L1)
            {
              name: 'l1',
              dryBulbTemp: ahuCalculationResults.supplyAir.dryBulbTemp,
              relativeHumidity: ahuCalculationResults.supplyAir.relativeHumidity,
              color: '#96CEB4',
              size: 6
            },
            // 냉방용 급기상태점 (SA)
            {
              name: '급기(SA)',
              dryBulbTemp: ahuCalculationResults.supplyAir.dryBulbTemp + (ahuCalculationResults.supplyFanTempRise || 0) + rhReheatTemp,
              relativeHumidity: ahuCalculationResults.supplyAir.relativeHumidity,
              humidityRatio: ahuCalculationResults.supplyAir.humidityRatio,
              color: '#81C784',
              size: 6,
              labelPosition: 'bottom-right' as const
            }
          ];

          // ADP 상태점 추가 (조건부)
          if (ahuCalculationResults.adp) {
            psychroChartPoints.push({
              name: 'adp',
              displayName: `adp${!ahuCalculationResults.adp.valid ? ' (계산불가!)' : ''}`,
              dryBulbTemp: ahuCalculationResults.adp.temperature,
              humidityRatio: ahuCalculationResults.adp.humidityRatio,
              relativeHumidity: ahuCalculationResults.adp.relativeHumidity,
              color: ahuCalculationResults.adp.valid ? '#FECA57' : '#FF3838',
              size: 6,
              labelPosition: 'bottom-right' as const
            });
          }

          // 난방 상태점들 추가
          const selectedCondition = OUTDOOR_CONDITIONS.find(
            (c: any) => c.code === (outdoorDesignCondition || 'A')
          );

          if (selectedCondition && ahuCalculationResults.heatingOutdoorAir) {
            // Heating OA (O2) - ahuCalculationResults의 정확한 값 사용
            psychroChartPoints.push({
              name: 'o2',
              dryBulbTemp: ahuCalculationResults.heatingOutdoorAir.dryBulbTemp,
              relativeHumidity: ahuCalculationResults.heatingOutdoorAir.relativeHumidity,
              humidityRatio: ahuCalculationResults.heatingOutdoorAir.humidityRatio,
              color: '#3F51B5',
              size: 6
            });

            // Heating RA (R2) - ahuCalculationResults의 정확한 값 사용
            if (ahuCalculationResults.heatingReturnAir) {
              psychroChartPoints.push({
                name: 'r2',
                dryBulbTemp: ahuCalculationResults.heatingReturnAir.dryBulbTemp,
                relativeHumidity: ahuCalculationResults.heatingReturnAir.relativeHumidity,
                humidityRatio: ahuCalculationResults.heatingReturnAir.humidityRatio,
                color: '#9C27B0',
                size: 6,
                labelPosition: 'bottom-right' as const
              });
            }

            // Heating MA (m1) - ahuCalculationResults의 정확한 값 사용
            if (ahuCalculationResults.heatingMixedAir) {
              psychroChartPoints.push({
                name: 'm1',
                dryBulbTemp: ahuCalculationResults.heatingMixedAir.dryBulbTemp,
                relativeHumidity: ahuCalculationResults.heatingMixedAir.relativeHumidity,
                humidityRatio: ahuCalculationResults.heatingMixedAir.humidityRatio,
                color: '#673AB7',
                size: 6,
                labelPosition: 'bottom-right' as const
              });
            }

            // Heating L2 (난방 급기) - ahuCalculationResults의 정확한 값 사용
            if (ahuCalculationResults.heatingSupplyAir) {
              psychroChartPoints.push({
                name: 'l2',
                dryBulbTemp: ahuCalculationResults.heatingSupplyAir.dryBulbTemp,
                relativeHumidity: ahuCalculationResults.heatingSupplyAir.relativeHumidity,
                humidityRatio: ahuCalculationResults.heatingSupplyAir.humidityRatio,
                color: '#FF9800',
                size: 6
              });
            }

            // Heating L3 (난방 가열 후) - ahuCalculationResults의 정확한 값 사용
            if (ahuCalculationResults.heatingAfterHeating) {
              psychroChartPoints.push({
                name: 'l3',
                dryBulbTemp: ahuCalculationResults.heatingAfterHeating.dryBulbTemp,
                relativeHumidity: ahuCalculationResults.heatingAfterHeating.relativeHumidity,
                humidityRatio: ahuCalculationResults.heatingAfterHeating.humidityRatio,
                color: '#4CAF50',
                size: 6
              });
            }
          }

          // 기본 라인들 - 냉방(파란색), 난방(빨간색) 색상 완전 통일
          const COOLING_COLOR = '#2196F3'; // 파란색 (냉방 전용)
          const HEATING_COLOR = '#F44336'; // 빨간색 (난방 전용)

          const baseLines = [
            // 냉방 프로세스 라인들 (모두 동일한 파란색)
            // 외기 → 혼합공기
            { from: '외기(OA)', to: '혼합(MA)', color: COOLING_COLOR, width: 2.5, type: 'mixing' },
            // 환기 → 혼합공기
            { from: '환기(RA)', to: '혼합(MA)', color: COOLING_COLOR, width: 2.5, type: 'mixing' },
            // 혼합공기 → 냉각코일 후
            { from: '혼합(MA)', to: 'l1', color: COOLING_COLOR, width: 2.5, type: 'cooling' },
            // 냉각코일 후 → 급기
            { from: 'l1', to: '급기(SA)', color: COOLING_COLOR, width: 2.5, type: 'process' },
            // 급기 → 환기 (실내 부하)
            { from: '급기(SA)', to: '환기(RA)', color: COOLING_COLOR, width: 2.5, type: 'process' }
          ];

          // ADP가 있을 때 ADP-L1 냉각 선 추가 (valid 여부와 관계없이)
          if (ahuCalculationResults.adp) {
            baseLines.push(
              { from: 'l1', to: 'adp', color: COOLING_COLOR, width: 2.5, type: 'cooling' }
            );
          }

          // 난방 라인들 추가 (모두 동일한 빨간색)
          if (selectedCondition && supplyAirflow > 0) {
            baseLines.push(
              // 난방용 외기 → 난방용 혼합공기
              { from: 'o2', to: 'm1', color: HEATING_COLOR, width: 2.5, type: 'heating' },
              // 난방용 환기 → 난방용 혼합공기
              { from: 'r2', to: 'm1', color: HEATING_COLOR, width: 2.5, type: 'heating' },
              // 난방용 혼합공기 → 가열 후 (l3)
              { from: 'm1', to: 'l3', color: HEATING_COLOR, width: 2.5, type: 'heating' },
              // 가열 후 → 가습 후 (l2)
              { from: 'l3', to: 'l2', color: HEATING_COLOR, width: 2.5, type: 'process' },
              // 난방용 급기 → 난방용 환기
              { from: 'l2', to: 'r2', color: HEATING_COLOR, width: 2.5, type: 'process' }
            );
          }

          const psychroChartLines = baseLines;

          // 캡처 시점의 AHU ID를 클로저로 고정 (비동기 처리 중 공조기 전환 방지)
          const captureTimeAHUId = currentAHUId;

          return (
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fff' }}>
              <PsychrometricChart
                points={psychroChartPoints}
                lines={psychroChartLines}
                elevation={parseFloat(currentProject?.altitude || '0')}
                winterOutdoorTemp={selectedCondition?.winter.temperature || -12}
                humidifierType={humidifierType}
                raLightingLoadMode={(() => {
                  const rawValue = watch('ventilationLightingLoad');
                  const cleanedValue = String(rawValue || '').replace(/,/g, '');
                  const ventilationLightingLoad = parseFloat(cleanedValue) || 0;
                  const mode = ventilationLightingLoad > 0 ? 'separate' : 'combined';
                  console.log('🔴🔴🔴 [AirflowSelectionTab] raw:', rawValue, 'cleaned:', cleanedValue, 'parsed:', ventilationLightingLoad, 'mode:', mode);
                  return mode;
                })()}
                // 실제 계산된 풍량 값들 전달
                saFlow={Math.round(calculatedAirflows.supplyAirflow)}
                raFlow={Math.round(realTimeReturnAirflow)}
                eaFlow={Math.round(parseFloat(watch('exhaustAirflow')) || 0)}
                oaFlowMin={Math.round(realTimeOutdoorAirflow.outdoorAirflow)}
                oaFlowMax={Math.round(calculatedAirflows.supplyAirflow)}
                chartId={captureTimeAHUId ? `psychrometric-chart-${captureTimeAHUId}` : 'psychrometric-chart'}
                showStatePoints={true}  // 상태점 정보를 차트 컴포넌트 내부에 표시 (이미지 캡처 시에는 제외됨)
                onChartCaptured={(capturedData) => {
                  // 캡처된 고해상도 이미지와 JSON 데이터를 상위로 전달
                  // captureTimeAHUId를 사용하여 캡처 시점의 공조기에 저장
                  if (onPsychroDataCalculated) {
                    onPsychroDataCalculated({
                      points: capturedData.points,
                      lines: capturedData.lines,
                      image: capturedData.image,
                      metadata: capturedData.metadata,
                      ahuId: captureTimeAHUId  // 클로저로 고정된 AHU ID 사용
                    });
                  }
                }}
              />
            </Box>
          );
        })()}
      </Grid>
    </Grid>
  )
}

export default AirflowSelectionTab